<script setup lang="ts">
import {
  PhArrowRight as ArrowRightIcon,
  PhCheckCircle as CheckCircleIcon,
  PhFileText as FileTextIcon,
  PhHandCoins as HandCoinsIcon,
  PhRuler as RulerIcon,
  PhShieldCheck as ShieldCheckIcon,
} from '@phosphor-icons/vue'
import { onMounted, ref } from 'vue'
import ProductCard from '../components/ProductCard.vue'
import ProductImage from '../components/ProductImage.vue'
import StatusState from '../components/StatusState.vue'
import { api } from '../services/api'
import type { Product } from '../types'

const products = ref<Product[]>([])
const loading = ref(true)
const error = ref('')

async function loadProducts() {
  loading.value = true
  error.value = ''
  try {
    const loadedProducts = await api.listProducts()
    products.value = [...loadedProducts]
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .filter((_, index) => index < 3)
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '产品加载失败'
  } finally {
    loading.value = false
  }
}

onMounted(loadProducts)
</script>

<template>
  <section class="hero">
    <div class="container hero__grid">
      <div class="hero__content">
        <h1>让每一寸空间，都更懂生活</h1>
        <p>根据你的空间与生活习惯，找到好看、实用、能够放心落地的柜类定制方案。</p>
        <div class="hero__actions">
          <RouterLink class="button button--primary button--large" to="/products">
            现有款式定制
            <ArrowRightIcon :size="19" aria-hidden="true" />
          </RouterLink>
          <RouterLink class="button button--secondary button--large" to="/custom-design">
            来图定制
          </RouterLink>
        </div>
      </div>
      <div class="hero__visual">
        <ProductImage
          src="/assets/cabinet-hero.avif"
          alt="现代卧室中的浅色定制衣柜实景效果"
          position="center"
          eager
        />
        <div class="hero__visual-note">
          <ShieldCheckIcon :size="21" weight="duotone" aria-hidden="true" />
          <div>
            <strong>生产前四项确认</strong>
            <span>尺寸、方案、材质五金、生产规则</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="trust-strip" aria-label="服务承诺摘要">
    <div class="container trust-strip__grid">
      <div>
        <RulerIcon :size="23" weight="duotone" aria-hidden="true" />
        <span><strong>专业师傅上门量房</strong>现场信息形成版本报告</span>
      </div>
      <div>
        <FileTextIcon :size="23" weight="duotone" aria-hidden="true" />
        <span><strong>图纸确认</strong>变更后必须重新确认</span>
      </div>
      <div>
        <HandCoinsIcon :size="23" weight="duotone" aria-hidden="true" />
        <span><strong>先确认方案再付款</strong>上传凭证不等于到账</span>
      </div>
      <div>
        <CheckCircleIcon :size="23" weight="duotone" aria-hidden="true" />
        <span><strong>每步操作均可查询</strong>每个关键动作可追溯</span>
      </div>
    </div>
  </section>

  <section class="section section--subtle">
    <div class="container">
      <div class="section-heading">
        <h2>从现有柜款开始选择</h2>
        <p>所有颜色、尺寸和五金都是验证过可以落地的选项，选好即可提交。</p>
      </div>
      <StatusState v-if="loading" kind="loading" />
      <StatusState
        v-else-if="error"
        kind="error"
        title="产品暂时无法显示"
        :message="error"
        @retry="loadProducts"
      />
      <StatusState v-else-if="products.length === 0" kind="empty" title="产品正在准备中">
        <RouterLink class="button button--secondary" to="/trust">先了解服务流程</RouterLink>
      </StatusState>
      <div v-else class="product-grid">
        <ProductCard v-for="product in products" :key="product.id" :product="product" />
      </div>
      <div v-if="products.length" class="section-action">
        <RouterLink class="button button--secondary" to="/products">
          查看全部产品
          <ArrowRightIcon :size="18" aria-hidden="true" />
        </RouterLink>
      </div>
    </div>
  </section>

  <!-- 插入位置：HomeView.vue -->
  <!-- 放在"从现有柜款开始选择"section 和"每一步都知道下一步是什么"section 之间 -->
  <section class="section section--subtle">
    <div class="container">
      <div class="section-heading">
        <h2>现有款式，填好尺寸直接报价</h2>
        <p>我们有成熟款式的完整 CAD 模型。选好颜色和五金，留下微信，24 小时内收到含效果图的报价。</p>
      </div>
      <div class="service-flow">
        <div class="service-flow__step">
          <span class="service-flow__num">01</span>
          <h3>选款式和配置</h3>
          <p>从现有柜款里选颜色、标准尺寸或填写定制尺寸、五金方案。</p>
        </div>
        <span class="service-flow__arrow" aria-hidden="true">→</span>
        <div class="service-flow__step">
          <span class="service-flow__num">02</span>
          <h3>留下联系微信</h3>
          <p>提交时填写微信号或手机，告知所在省市，全程线上沟通。</p>
        </div>
        <span class="service-flow__arrow" aria-hidden="true">→</span>
        <div class="service-flow__step">
          <span class="service-flow__num">03</span>
          <h3>24 小时内收到报价</h3>
          <p>客服通过微信发送含 CAD 效果图的详细报价，确认后进入量房流程。</p>
        </div>
      </div>
      <div class="section-action">
        <RouterLink class="button button--primary" to="/products">
          立即选款
          <ArrowRightIcon :size="18" aria-hidden="true" />
        </RouterLink>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-heading">
        <h2>每一步都知道下一步是什么</h2>
        <p>每一步做什么、等什么，都在订单进度里写清楚。</p>
      </div>
      <div class="journey">
        <article>
          <span>选择与询价</span>
          <h3>先确认能否服务您</h3>
          <p>填写城市和定制需求后，系统会先核对是否在服务范围内，不会接了再拒。</p>
        </article>
        <article>
          <span>量房与方案</span>
          <h3>以现场报告为准</h3>
          <p>师傅上门拍照、量尺寸，所有数据形成报告，后续报价和图纸都以这份报告为准。</p>
        </article>
        <article>
          <span>确认与付款</span>
          <h3>看清资料再确认</h3>
          <p>您一次查看尺寸、图纸、材质五金和生产规则，确认后再付款，付款到账由财务核实。</p>
        </article>
        <article>
          <span>最终复核</span>
          <h3>复核完成才开始生产</h3>
          <p>所有条件满足后，生产方案锁定，不会在生产途中再改动。</p>
        </article>
      </div>
    </div>
  </section>

  <section class="section section--compact">
    <div class="container assurance-cta">
      <div>
        <ShieldCheckIcon :size="34" weight="duotone" aria-hidden="true" />
        <h2>了解我们怎么保障您的权益</h2>
        <p>企业证照、服务范围、付款保障，看清楚再决定。</p>
      </div>
      <RouterLink class="button button--primary" to="/trust">查看服务保障</RouterLink>
    </div>
  </section>
</template>
